import React, { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Modal } from "./ui-modal";
import { fmtDateTime } from "../lib/helpers";

// Notification contextuelle réutilisable.
// props: contextType, contextId, contextLabel, recipients [{id,name,role}], label
export default function ContactButton({ contextType, contextId, contextLabel = "", recipients = [], label = "Contacter", testid = "contact" }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);

  const loadHistory = async () => {
    try { const { data } = await api.get("/admin/notify/history", { params: { context_type: contextType, context_id: contextId } }); setHistory(data); } catch { /* */ }
  };
  const openModal = async () => { setSelected(recipients.map((r) => r.id)); setSubject(""); setMessage(""); setOpen(true); loadHistory(); };
  const toggle = (rid) => setSelected((s) => s.includes(rid) ? s.filter((x) => x !== rid) : [...s, rid]);
  const send = async () => {
    if (!message.trim()) return toast.error("Le message est vide");
    if (selected.length === 0) return toast.error("Sélectionnez au moins un destinataire");
    setSending(true);
    try {
      const { data } = await api.post("/admin/notify", { recipient_ids: selected, subject, message, context_type: contextType, context_id: contextId, context_label: contextLabel });
      toast.success(`Notification envoyée (${data.count})`);
      setMessage(""); setSubject(""); loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
    finally { setSending(false); }
  };
  const roleLabel = (r) => r.role === "collaborator" ? "Collaborateur" : r.role === "client" ? "Entreprise" : "";

  return (
    <>
      <button onClick={openModal} data-testid={`${testid}-btn`} className="inline-flex items-center gap-1.5 text-sm border border-marine/30 text-marine rounded-sm px-3 py-2 hover:bg-marine hover:text-white transition-colors">
        <Icons.Send size={16} /> {label}
      </button>
      {open && (
        <Modal onClose={() => setOpen(false)} title="Envoyer une notification" wide>
          <div className="space-y-4" data-testid="contact-modal">
            <div>
              <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold mb-2">Destinataire(s)</div>
              {recipients.length === 0 ? (
                <p className="text-sm text-rouge">Aucun destinataire disponible pour cet élément.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {recipients.map((r) => {
                    const on = selected.includes(r.id);
                    return (
                      <button key={r.id} onClick={() => toggle(r.id)} data-testid={`recipient-${r.id}`}
                        className={`flex items-center gap-1.5 rounded-full pl-1.5 pr-3 py-1 text-sm border transition-colors ${on ? "bg-marine text-white border-marine" : "bg-sable text-marine border-marine/20"}`}>
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-display ${on ? "bg-white/20" : "bg-marine text-white"}`}>{r.name?.[0]?.toUpperCase()}</span>
                        {r.name}{roleLabel(r) ? <span className={`text-[10px] uppercase ${on ? "text-white/70" : "text-bordeaux"}`}>{roleLabel(r)}</span> : null}
                        {on && <Icons.Check size={16} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <label className="text-sm block">Objet (optionnel)<input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="contact-subject" className="inp mt-1" placeholder="Objet du message" /></label>
            <label className="text-sm block">Message<textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="contact-message" rows={4} className="inp mt-1 w-full resize-y" placeholder="Votre message..." /></label>
            <div className="flex justify-end"><button onClick={send} disabled={sending} data-testid="contact-send" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"><Icons.Send size={16} /> {sending ? "Envoi..." : "Envoyer"}</button></div>

            <div className="pt-4 border-t border-marine/10">
              <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold mb-2">Historique des échanges</div>
              {history.length === 0 ? <p className="text-sm text-marine/80">Aucune notification envoyée pour cet élément.</p> : (
                <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="contact-history">
                  {history.map((h) => (
                    <div key={h.id} className="rounded-sm border border-marine/10 p-2.5 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-marine truncate">{h.subject}</span>
                        <span className={`text-xs shrink-0 ${h.read ? "text-bleu" : "text-ambre-texte"}`}>{h.read ? "Lu" : "Non lu"}</span>
                      </div>
                      <div className="text-marine">{h.body}</div>
                      <div className="text-xs text-marine/80 mt-0.5">{h.sender_name} → {h.recipient_name} · {fmtDateTime(h.created_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
